import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, CheckCircle, XCircle, RotateCcw, AlertTriangle } from "lucide-react";
import { ResponsiveModal } from "@wtc/ui";

export interface BulkActionProgressItem {
  id: string;
  name: string;
  status: "pending" | "processing" | "success" | "error";
  error?: string;
  retryCount?: number;
}

export interface BulkActionProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  totalItems: number;
  items: BulkActionProgressItem[];
  onRetryItem: (item: BulkActionProgressItem) => void;
  onRetryAllFailed: () => void;
  isProcessing: boolean;
}

export function BulkActionProgressModal({
  isOpen,
  onClose,
  title,
  totalItems,
  items,
  onRetryItem,
  onRetryAllFailed,
  isProcessing,
}: BulkActionProgressModalProps) {
  const [isClosing, setIsClosing] = useState(false);

  const completedCount = items.filter((item) => item.status === "success").length;
  const errorCount = items.filter((item) => item.status === "error").length;
  const processingCount = items.filter((item) => item.status === "processing").length;
  const pendingCount = items.filter((item) => item.status === "pending").length;

  const progress = totalItems > 0 ? (completedCount / totalItems) * 100 : 0;

  const failedItems = items.filter((item) => item.status === "error");
  const hasFailures = errorCount > 0;
  const isComplete = completedCount + errorCount === totalItems && !isProcessing;

  const handleClose = useCallback(() => {
    if (isProcessing && !isComplete) {
      setIsClosing(true);
      // Show confirmation dialog
      const confirmed = window.confirm(
        "The bulk action is still in progress. Are you sure you want to close this window? The operation will continue in the background."
      );
      if (!confirmed) {
        setIsClosing(false);
        return;
      }
    }
    setIsClosing(false);
    onClose();
  }, [isProcessing, isComplete, onClose]);

  // Don't auto-close - let user manually close the modal
  // This allows users to see the final status and manually close when ready

  const getStatusIcon = (status: BulkActionProgressItem["status"]) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "processing":
        return (
          <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        );
      default:
        return <div className="h-4 w-4 border border-gray-300 dark:border-gray-700 rounded-full" />;
    }
  };

  const getStatusBadge = (status: BulkActionProgressItem["status"]) => {
    switch (status) {
      case "success":
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
            Success
          </Badge>
        );
      case "error":
        return <Badge variant="destructive">Failed</Badge>;
      case "processing":
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
            Processing
          </Badge>
        );
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={handleClose}
      title={title}
      maxWidth="2xl"
      side="bottom"
    >
      <div className="space-y-6">
        {/* Progress Overview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Progress: {completedCount} of {totalItems} completed
            </div>
            <div className="text-sm font-medium">{Math.round(progress)}%</div>
          </div>

          <Progress value={progress} className="h-2" />

          {/* Status Summary */}
          <div className="flex items-center gap-4 text-sm">
            {completedCount > 0 && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>{completedCount} completed</span>
              </div>
            )}
            {processingCount > 0 && (
              <div className="flex items-center gap-1 text-blue-600">
                <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>{processingCount} processing</span>
              </div>
            )}
            {errorCount > 0 && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="h-4 w-4" />
                <span>{errorCount} failed</span>
              </div>
            )}
            {pendingCount > 0 && (
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                <div className="h-4 w-4 border border-gray-300 dark:border-gray-700 rounded-full" />
                <span>{pendingCount} pending</span>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {hasFailures && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {errorCount} item{errorCount === 1 ? "" : "s"} failed to process. You can retry
              individual items or all failed items at once.
            </AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        {isComplete && !hasFailures && (
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              All {totalItems} item{totalItems === 1 ? "" : "s"} processed successfully!
            </AlertDescription>
          </Alert>
        )}

        {/* Items List */}
        {items.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Items ({items.length})</div>
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-2 border rounded bg-card-50"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getStatusIcon(item.status)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-card-foreground truncate">
                        {item.name}
                      </div>
                      {item.error && (
                        <div className="text-xs text-red-600 truncate">{item.error}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {getStatusBadge(item.status)}

                    {item.status === "error" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRetryItem(item)}
                        className="gap-1 h-7 px-2"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            {hasFailures && (
              <Button variant="outline" onClick={onRetryAllFailed} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Retry All Failed ({errorCount})
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isComplete && (
              <Button onClick={handleClose} className="gap-2">
                <CheckCircle className="h-4 w-4" />
                Close
              </Button>
            )}
            {!isComplete && (
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isProcessing}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                {isProcessing ? "Close (Continue in Background)" : "Close"}
              </Button>
            )}
            {!isComplete && !isProcessing && (
              <div className="text-sm text-gray-500">
                Processing complete - you can close this window
              </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}
